import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { signOut, supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useOwner } from '../../hooks/useOwner';
import { useTheme } from '../../hooks/useTheme';
import { BrandLogo } from '../common/BrandLogo';
import {
  LayoutDashboard, Car, FileText, Wrench, Shield,
  Receipt, DollarSign, Bell, BarChart3, Settings,
  LogOut, Fuel, Brain, Users, Moon, PieChart,
  ChevronDown, CreditCard, ClipboardList, Upload
} from 'lucide-react';

// Top-level items — no section label, shown in this exact order.
const topItems = [
  { to: '/vehicles', icon: Car, label: 'Vehicles', roles: ['owner','admin','editor','viewer'] },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['owner','admin','editor','viewer'] },
  { to: '/portfolio', icon: PieChart, label: 'Portfolio', roles: ['owner','admin','editor','viewer'] },
  { to: '/classic-car-intelligence', icon: Brain, label: 'Car Intelligence', roles: ['owner','admin','editor','viewer'] },
  { to: '/alerts', icon: Bell, label: 'Alerts', roles: ['owner','admin','editor','viewer'] },
];

// "Documents" dropdown — collapsed by default, auto-opens when one of its
// own pages is the active route.
const documentsItems = [
  { to: '/reports', icon: BarChart3, label: 'Reports', roles: ['owner','admin','editor','viewer'] },
  { to: '/expenses', icon: DollarSign, label: 'Expenses', roles: ['owner','admin','editor','viewer'] },
  { to: '/insurance', icon: Shield, label: 'Insurance', roles: ['owner','admin','editor','viewer'] },
  { to: '/road-tax', icon: Receipt, label: 'Road Tax', roles: ['owner','admin','editor','viewer'] },
  { to: '/services', icon: Wrench, label: 'Services', roles: ['owner','admin','editor','viewer'] },
  { to: '/mechanical', icon: Fuel, label: 'Mechanical', roles: ['owner','admin','editor','viewer'] },
  { to: '/documents', icon: FileText, label: 'Documents', roles: ['owner','admin','editor','viewer'] },
  { to: '/forms', icon: ClipboardList, label: 'Forms & Checklists', roles: ['owner','admin','editor','viewer'] },
];

const systemItems = [
  { to: '/import', icon: Upload, label: 'Bulk Import', roles: ['owner','admin'] },
  { to: '/team', icon: Users, label: 'Team', roles: ['owner','admin'] },
  // Moved out of Settings so billing/plan management has its own place in the
  // main menu — sits between Team and Settings.
  { to: '/billing', icon: CreditCard, label: 'Billing', roles: ['owner'] },
  { to: '/settings', icon: Settings, label: 'Settings', roles: ['owner'] },
];

const roleConfig = {
  owner:  { label: 'Owner',  color: '#eab308' },
  admin:  { label: 'Admin',  color: '#4f8ef7' },
  editor: { label: 'Editor', color: '#22c55e' },
  viewer: { label: 'Viewer', color: '#a855f7' },
};

// Shared row renderer for every nav item — top-level, Documents dropdown, and
// System alike — so the gradient icon badge, active-state styling and the
// /settings pending-access badge stay identical across all three groups.
const renderItems = (items, role, onNavigate, pendingAccess, unpaidInvoices = 0) => {
  const visibleItems = items.filter(item => !role || item.roles.includes(role));
  return visibleItems.map(item => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === '/'}
      style={{ textDecoration: 'none' }}
    >
      {({ isActive }) => (
        <div
          onClick={() => onNavigate && onNavigate()}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px', borderRadius: 12, marginBottom: 2,
            cursor: 'pointer', transition: 'all 0.15s',
            background: isActive
              ? 'linear-gradient(135deg, rgba(79,142,247,0.15), rgba(124,58,237,0.1))'
              : 'transparent',
            border: isActive
              ? '1px solid rgba(79,142,247,0.2)'
              : '1px solid transparent',
          }}
          onMouseEnter={e => {
            if (!isActive) {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
            }
          }}
          onMouseLeave={e => {
            if (!isActive) {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          {/* Gradient icon badge */}
          <div style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            background: '#4f8ef7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isActive ? '0 4px 12px rgba(79,142,247,0.5)' : '0 2px 6px rgba(79,142,247,0.25)',
          }}>
            <item.icon size={15} style={{ color: 'white' }} />
          </div>

          {/* Label — single colour always */}
          <span style={{
            fontSize: 13.5,
            fontWeight: isActive ? 600 : 400,
            color: isActive ? 'white' : 'rgba(255,255,255,0.55)',
            transition: 'all 0.15s',
            letterSpacing: 0.1,
          }}>
            {item.label}
          </span>
          {item.to === '/alerts' && unpaidInvoices > 0 && (
            <span title={`${unpaidInvoices} invoice${unpaidInvoices !== 1 ? 's' : ''} awaiting payment`}
              style={{
                marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px',
                borderRadius: 9, background: '#f97316', color: '#fff',
                fontSize: 11, fontWeight: 700, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{unpaidInvoices}</span>
          )}
          {item.to === '/settings' && pendingAccess > 0 && (
            <span title={`${pendingAccess} support access request${pendingAccess !== 1 ? 's' : ''} awaiting your approval`}
              style={{
                marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px',
                borderRadius: 9, background: '#f97316', color: '#fff',
                fontSize: 11, fontWeight: 700, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{pendingAccess}</span>
          )}
        </div>
      )}
    </NavLink>
  ));
};

export const Sidebar = ({ onNavigate }) => {
  const { user } = useAuth();
  const { role } = useOwner();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const documentsHasActive = documentsItems.some(item => item.to === location.pathname);
  const [docsOpen, setDocsOpen] = useState(documentsHasActive);
  // Jumping straight to a Documents page (a link, a refresh) should always
  // reveal it in the sidebar, even if the dropdown was previously collapsed.
  useEffect(() => {
    if (documentsHasActive) setDocsOpen(true);
  }, [documentsHasActive]);

  // Pending support-access requests need to be visible from anywhere — a request
  // sitting unseen in Settings is a request that never gets approved.
  const [pendingAccess, setPendingAccess] = useState(0);
  useEffect(() => {
    if (!user?.id) return;
    let stop = false;
    const check = async () => {
      const { count, error } = await supabase
        .from('support_access')
        .select('id', { count: 'exact', head: true })
        .eq('owner_id', user.id)
        .eq('status', 'pending');
      if (!stop && !error) setPendingAccess(count || 0);
    };
    check();
    const timer = setInterval(check, 60000);   // re-check every minute
    return () => { stop = true; clearInterval(timer); };
  }, [user?.id]);

  // Invoices recorded as "awaiting payment" show as a badge on Alerts.
  const [unpaidInvoices, setUnpaidInvoices] = useState(0);
  useEffect(() => {
    if (!user?.id) return;
    let stop = false;
    const check = async () => {
      const { count, error } = await supabase
        .from('expenses')
        .select('id', { count: 'exact', head: true })
        .eq('payment_status', 'unpaid');
      if (!stop && !error) setUnpaidInvoices(count || 0);
    };
    check();
    const timer = setInterval(check, 60000);
    return () => { stop = true; clearInterval(timer); };
  }, [user?.id]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const rc = roleConfig[role] || roleConfig.viewer;
  const initial = user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <aside style={{
      width: 'var(--sidebar-width)',
      background: 'linear-gradient(180deg, #0f1535 0%, #090d20 100%)',
      borderRight: '1px solid rgba(255,255,255,0.06)',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      zIndex: 100,
    }}>

      {/* Logo */}
      <div style={{ padding: '28px 16px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/portfolio')} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
          <BrandLogo variant="white" width={172} />
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
        {renderItems(topItems, role, onNavigate, pendingAccess, unpaidInvoices)}

        {/* Documents — collapsible dropdown */}
        {(() => {
          const visibleDocs = documentsItems.filter(item => !role || item.roles.includes(role));
          if (visibleDocs.length === 0) return null;
          return (
            <div style={{ marginBottom: 8 }}>
              <div
                onClick={() => setDocsOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  padding: '10px 14px 6px', userSelect: 'none',
                }}
              >
                <span style={{
                  fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: 1.5, color: 'rgba(255,255,255,0.3)', flex: 1,
                }}>
                  Documents
                </span>
                <ChevronDown
                  size={13}
                  style={{
                    color: 'rgba(255,255,255,0.3)',
                    transform: docsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s',
                  }}
                />
              </div>
              {docsOpen && renderItems(visibleDocs, role, onNavigate, pendingAccess)}
            </div>
          );
        })()}

        {/* System */}
        {(() => {
          const visibleSystem = systemItems.filter(item => !role || item.roles.includes(role));
          if (visibleSystem.length === 0) return null;
          return (
            <div style={{ marginBottom: 8 }}>
              <div style={{
                fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: 1.5, color: 'rgba(255,255,255,0.3)',
                padding: '10px 14px 6px',
              }}>
                System
              </div>
              {renderItems(visibleSystem, role, onNavigate, pendingAccess)}
            </div>
          );
        })()}
      </nav>

      {/* Footer */}
      <div style={{ padding: '12px 12px 20px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          style={{
            width: '100%', marginBottom: 8, padding: '10px 14px',
            borderRadius: 12, cursor: 'pointer',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', gap: 12,
            fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
            color: 'rgba(255,255,255,0.55)',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
        >
          <div style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(79,142,247,0.4)',
          }}>
            <Moon size={15} style={{ color: 'white' }} />
          </div>
          <span style={{ fontSize: 13.5, fontWeight: 400, color: 'rgba(255,255,255,0.55)' }}>
            Dark Mode
          </span>
          {/* On / off switch */}
          <div style={{
            marginLeft: 'auto', width: 34, height: 18, borderRadius: 9,
            background: theme === 'dark' ? '#4f8ef7' : 'rgba(255,255,255,0.15)',
            position: 'relative', transition: 'background 0.2s', flexShrink: 0,
          }}>
            <div style={{
              position: 'absolute', top: 2,
              left: theme === 'dark' ? 18 : 2,
              width: 14, height: 14, borderRadius: '50%',
              background: 'white', transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
        </button>

        {/* User card */}
        <div style={{
          padding: '12px 14px', borderRadius: 12, marginBottom: 8,
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, color: 'white',
            boxShadow: '0 4px 10px rgba(79,142,247,0.4)',
          }}>
            {initial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user?.email}
            </div>
            {role && (
              <div style={{ marginTop: 2 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 8px',
                  borderRadius: 20,
                  background: `${rc.color}22`,
                  color: rc.color,
                  border: `1px solid ${rc.color}44`,
                }}>
                  {rc.label}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Sign out */}
        <button
          onClick={handleSignOut}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 12,
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.35)', fontSize: 13, fontWeight: 400,
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8,
            fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)';
            e.currentTarget.style.color = '#ef4444';
            e.currentTarget.style.background = 'rgba(239,68,68,0.08)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.color = 'rgba(255,255,255,0.35)';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <LogOut size={13} />
          Sign Out
        </button>
      </div>
    </aside>
  );
};